/**
 * Bulk Assignment Modal Component
 * After creating/updating a vehicle assignment, this modal allows
 * the user to apply the assignment to multiple segments at once.
 */

import React, { useState } from 'react';
import { PlanningSegment, VehicleAssignment } from '../../types/matchdayPlanning';

interface BulkAssignmentModalProps {
  assignment: VehicleAssignment;
  segments: PlanningSegment[];
  onApply: (segmentIds: number[], assignmentId: number) => Promise<void>;
  onClose: () => void;
}

const BulkAssignmentModal: React.FC<BulkAssignmentModalProps> = ({
  assignment,
  segments,
  onApply,
  onClose
}) => {
  // Filter out main event (kickoff) and stopover segments (no vehicle needed)
  const eligibleSegments = segments.filter(s => !s.isMainEvent && s.anchorType !== 'main' && s.anchorType !== 'stopover');

  // Pre-select segments that already have this assignment
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(eligibleSegments.filter(s => s.vehicleAssignmentId === assignment.id).map(s => s.id))
  );
  const [applying, setApplying] = useState(false);

  const toggleSegment = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === eligibleSegments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibleSegments.map(s => s.id)));
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      await onApply(Array.from(selectedIds), assignment.id);
      onClose();
    } catch (error) {
      console.error('Error applying bulk assignment:', error);
      alert('Fehler beim Zuweisen. Bitte erneut versuchen.');
    } finally {
      setApplying(false);
    }
  };

  const getAssignmentLabel = (): string => {
    const parts: string[] = [];
    const vehicleName = assignment.transportation?.name || assignment.vehicle?.name || '';
    if (vehicleName) parts.push(vehicleName);
    if (assignment.driverType === 'traveler' && assignment.traveler) {
      parts.push(assignment.traveler.fullName);
    } else if (assignment.driverType === 'partnerDriver' && assignment.partnerDriver) {
      parts.push(assignment.partnerDriver.fullName);
    }
    if (assignment.partner) parts.push(assignment.partner.name);
    return parts.length > 0 ? parts.join(' · ') : `Fahrzeug #${assignment.displayOrder}`;
  };

  const formatTime = (time: string) => time?.substring(0, 5) || '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Zuordnung übernehmen</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ padding: '16px 24px' }}>
          <p style={{ marginBottom: '12px', color: '#495057' }}>
            <strong>{getAssignmentLabel()}</strong> auf folgende Segmente anwenden:
          </p>

          <div style={{ marginBottom: '12px', borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={selectedIds.size === eligibleSegments.length}
                onChange={toggleAll}
                style={{ width: '18px', height: '18px' }}
              />
              Alle auswählen ({selectedIds.size}/{eligibleSegments.length})
            </label>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {eligibleSegments.map(segment => (
              <label
                key={segment.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 4px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  background: selectedIds.has(segment.id) ? '#f0f7ff' : 'transparent',
                  borderRadius: '4px'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(segment.id)}
                  onChange={() => toggleSegment(segment.id)}
                  style={{ width: '18px', height: '18px', flexShrink: 0 }}
                />
                <span style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500 }}>{segment.location}</span>
                  <span style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '8px' }}>
                    {formatTime(segment.scheduledTime)}
                  </span>
                </span>
                {segment.vehicleAssignmentId === assignment.id && (
                  <span style={{ fontSize: '0.75rem', color: '#28a745' }}>✓ bereits</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={applying}>
            Abbrechen
          </button>
          <button type="button" className="btn btn-primary" onClick={handleApply} disabled={applying}>
            {applying ? 'Wird angewendet...' : `Zuweisen (${selectedIds.size})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignmentModal;

